/**
 * Member-level forecast UI (screenshots/26, 35, 41 cards 1–6 and 9).
 *  - InfluenceTable: "Who's lobbying, and how members are likely to vote" (Overview tab)
 *  - ForecastByMember: grouped toss-ups / unlikely / likely, with What-if mode (Forecast by member tab)
 * Each row subscribes to its own InfluenceRow, so a live `forecast.update` re-renders one row (L2).
 */
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useShallow } from "zustand/react/shallow";
import { ArrowDown, ArrowDownUp, ArrowRight, ArrowUp, Building2, Check, ChevronDown, Flag, FlaskConical } from "lucide-react";
import { Face } from "../components/shared/Face";
import { List, ListRow, RowCell, RowLead, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { ForecastChip, ForecastMeter, ForecastWhy, ProjectionBar, Roll, TensionGauge, chipText, pct } from "../forecast/ForecastViews";
import { enoughHistory, type Forecast, type ForecastLabel } from "../forecast/forecast";
import { NotEnoughHistory } from "../components/shared/States";
import { easing, flipFrom, prefersReducedMotion, recipes, reducedFallback } from "../motion";
import { leaningLabel } from "../people/types";
import { canComputeMoneyLinked, needsOverlapNote, positionLabel } from "../organizations/logic";
import type { LobbyingClient } from "./types";
import { groupByLabel, inPerspective, movedPoints, projectWithWhatIf, sortMembers, type MemberSort } from "./logic";
import { useLegislation, type LegislationStore } from "./legislationStore";
import { AiChip, CountBadge, GroupLabel, LinkArrow, ViewSwitch } from "./ui";
import type { InfluenceRow, LegislationDetail } from "./types";

const usd = (n: number) => (n ? `$${Math.round(n).toLocaleString("en-US")}` : "—");
const EMPTY: InfluenceRow[] = [];

/** Rows of the selected client (stable reference until a delta touches this client). */
export const useRows = (store: LegislationStore) =>
  useLegislation(store, (s) => (s.detail && s.clientId ? s.detail.lobbying.influence[s.clientId] ?? EMPTY : EMPTY));

export function useClient(store: LegislationStore) {
  return useLegislation(store, useShallow((s) => {
    const c = s.detail?.lobbying.clients.find((x) => x.id === s.clientId);
    return { client: c, myPosition: s.detail?.myPosition, perspective: s.perspective };
  }));
}

const shortName = (name: string) => (name.split(" ").length > 2 ? name.split(" ").map((w) => w[0]).join("") : name);
/** Money-linked votes need a STATED position (organizations/logic canComputeMoneyLinked); otherwise "None yet". */
export const moneyLinkedText = (r: InfluenceRow, c?: LobbyingClient) =>
  c && !canComputeMoneyLinked(c.positionBasis) ? "None yet"
    : r.moneyLinked.n ? `${r.moneyLinked.k} of ${r.moneyLinked.n}${r.moneyLinked.n < 2 ? " · too few" : " their way"}` : "—";

/** L1: the why panel grows beneath its row (220ms height); reduced motion = 100ms fade. */
function GrowIn({ children, className }: { children: React.ReactNode; className: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) { el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: reducedFallback.duration }); return; }
    const h = el.scrollHeight;
    el.animate([{ height: "0px", opacity: 0.4 }, { height: `${h}px`, opacity: 1 }], { duration: recipes.whyExpand.duration, easing: recipes.whyExpand.easing });
  }, []);
  return <div ref={ref} className={className}>{children}</div>;
}

function WhyPanel({ row, f, way, topic, inferred, recordId }: { row: InfluenceRow; f: Forecast; way: "their" | "your"; topic: string; inferred?: boolean; recordId?: string }) {
  return (
    <GrowIn className="why-panel lg-why">
      <ForecastWhy f={f} perspective={way} details={row.details} topic={topic} inferred={inferred}>
        <div className="why-panel__actions">
          <LinkArrow href={`/dashboard/people/${encodeURIComponent(row.personId)}/money`}>Open profile · Money tab</LinkArrow>
          <button className="lg-link lg-link--muted" onClick={(e) => {
            e.stopPropagation();
            void fetch("/api/dashboard/flags", { method: "POST", credentials: "include", headers: { "content-type": "application/json" },
              body: JSON.stringify({ kind: "forecast", recordId, personId: row.personId, inputs: row.inputs }) }).catch(() => {});
          }}>Flag as inaccurate<Flag size={14} aria-hidden /></button>
        </div>
      </ForecastWhy>
    </GrowIn>
  );
}

/** Client picker (L4): a listbox; rows re-rank in place (FLIP) when the client changes. */
function ClientPicker({ store, onSwitch }: { store: LegislationStore; onSwitch: (id: string) => void }) {
  const clients = useLegislation(store, (s) => s.detail?.lobbying.clients ?? null);
  const clientId = useLegislation(store, (s) => s.clientId);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    addEventListener("pointerdown", close); addEventListener("keydown", esc);
    return () => { removeEventListener("pointerdown", close); removeEventListener("keydown", esc); };
  }, [open]);
  const c = clients?.find((x) => x.id === clientId);
  if (!clients || !c) return null;
  const pos = (x: LobbyingClient) => { const l = positionLabel(x.position, x.positionBasis); return <span className={`chip lg-chip-11 chip--${l.tone}`}>{l.text}</span>; };
  return (
    <div className="lg-client" ref={root}>
      <button className="lg-client__btn" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Building2 size={14} aria-hidden /><b>{c.name}</b>{pos(c)}<ChevronDown size={14} aria-hidden />
      </button>
      {open && (
        <ul className="lg-client__menu" role="listbox" aria-label="Lobbying client">
          {clients.map((x) => (
            <li key={x.id} role="option" aria-selected={x.id === clientId} tabIndex={0}
              onClick={() => { setOpen(false); if (x.id !== clientId) onSwitch(x.id); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(false); if (x.id !== clientId) onSwitch(x.id); } }}>
              <span className="lg-client__check">{x.id === clientId && <Check size={14} aria-hidden />}</span>
              <span className="lg-client__name">{x.name}</span>{pos(x)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------------- Overview: influence table (26) ---------------- */

export function InfluenceTable({ store, canSwitchPerspective, onAll }: { store: LegislationStore; canSwitchPerspective: boolean; onAll: () => void }) {
  const rows = useRows(store);
  const sorted = useMemo(() => sortMembers(rows, "money").filter((r) => r.sinceAmount > 0).slice(0, 8), [rows]);
  const { client, myPosition } = useClient(store);
  const body = useRef<HTMLDivElement>(null);
  // The overview reads "their way" first (screenshots/26); the switch flips it in place (L3).
  const [perspective, setPerspective] = useState<"their" | "your">("their");
  if (!client) return null;

  const switchClient = (id: string) => {
    const done = body.current ? flipFrom(body.current) : () => {};
    flushSync(() => store.getState().setClient(id));
    done();
  };
  const way = perspective === "their" ? "their" : "your";

  return (
    <section className="influence" aria-label="Lobbying and vote forecast">
      <div className="section-head"><h2>Who's lobbying, and how members are likely to vote</h2><AiChip>Forecast</AiChip></div>
      <div className="influence__controls">
        <ClientPicker store={store} onSwitch={switchClient} />
        {canSwitchPerspective && myPosition && (
          <ViewSwitch label="Perspective" value={way} onChange={setPerspective}
            options={[{ id: "their", label: "Their way" }, { id: "your", label: `Your way (you ${myPosition})` }]} />
        )}
      </div>
      <div className="influence__table" role="table" aria-label="Members">
        <div className="influence__th" role="row">
          <span role="columnheader" className="c-who">Alderperson</span><span role="columnheader" className="c-since">Since 2019</span>
          <span role="columnheader" className="c-year">Past year</span><span role="columnheader" className="c-linked">Money-linked</span>
          <span role="columnheader" className="c-lean">Lean</span>
          <span role="columnheader" className="c-meter"><span className="lg-xfade" key={way}>Votes {way} way</span></span>
        </div>
        <div ref={body} role="rowgroup">{sorted.map((r) => <InfluenceRowView key={r.personId} store={store} personId={r.personId} perspective={perspective} />)}</div>
      </div>
      <div className="row-between lg-influence__foot">
        <LinkArrow onClick={onAll}>All {rows.length} members</LinkArrow>
        <LinkArrow muted href="/dashboard/forecast-method">How this forecast works</LinkArrow>
      </div>
      <p className="caveat">Money near a vote is not evidence that it influenced the vote. Contributions are from Illinois State Board of Elections and Board of Ethics filings, matched to the client by name.
        {sorted.some((r) => r.money && needsOverlapNote(r.money.since, "all")) && " Lobbyists' own contributions count under every client they represent."}</p>
    </section>
  );
}

/** The row's disclosure control for the "why" panel (a11y: aria-expanded lives on a button; the row stays a j/k stop). */
const RowToggle = ({ expanded, onToggle, children }: { expanded: boolean; onToggle: () => void; children: React.ReactNode }) => (
  <button type="button" className="row-toggle" aria-expanded={expanded} onClick={(e) => { e.stopPropagation(); onToggle(); }}>{children}</button>
);

const InfluenceRowView = memo(function InfluenceRowView({ store, personId, perspective }: { store: LegislationStore; personId: string; perspective: "their" | "your" }) {
  const recordId = useLegislation(store, (s) => s.detail?.recordId); // "Flag as inaccurate" names the forecast (BE1)
  const row = useLegislation(store, (s) => s.detail?.lobbying.influence[s.clientId ?? ""]?.find((r) => r.personId === personId));
  const expanded = useLegislation(store, (s) => s.expandedId === personId);
  const vote = useLegislation(store, (s) => s.detail?.rollCall?.votes[personId]);
  const topic = useLegislation(store, (s) => s.detail?.forecast?.topic ?? "this topic");
  const { client, myPosition } = useClient(store);
  if (!row || !client) return null;
  const f = inPerspective(row.forecast, perspective, client.position, myPosition);
  const way = perspective;
  return (
    <div data-flip-key={personId}>
      <div className="influence__tr" role="row" data-row={personId} tabIndex={0} data-open={expanded || undefined}
        onClick={() => store.getState().toggleExpanded(personId)}>
        <span className="c-who" role="cell"><Face name={row.name} photoUrl={row.photoUrl} size={28} />
          <span><RowToggle expanded={expanded} onToggle={() => store.getState().toggleExpanded(personId)}><b>{row.name}</b></RowToggle><small>Ward {row.ward}</small></span></span>
        <span className="c-since" role="cell">{usd(row.sinceAmount)}</span>
        <span className="c-year" role="cell">{usd(row.pastYearAmount)}</span>
        <span className="c-linked" role="cell">{moneyLinkedText(row, client)}</span>
        <span className="c-lean" role="cell">{leaningLabel(row.leaning)}</span>
        <span className="c-meter" role="cell">{vote ? <VoteChip vote={vote} /> : enoughHistory(row.inputs) ? <ForecastMeter f={f} perspective={way} /> : <NotEnoughHistory />}</span>
      </div>
      {expanded && <div role="row" className="why-row" onClick={(e) => { if (e.target === e.currentTarget) store.getState().toggleExpanded(personId); }}><div role="cell"><WhyPanel row={row} f={f} way={way} topic={topic} inferred={client.positionBasis === "inferred"} recordId={recordId} /></div></div>}
    </div>
  );
});

const VoteChip = ({ vote, delay = 0 }: { vote: string; delay?: number }) => (
  <span className={`chip lg-vote chip--${vote === "yea" ? "positive" : vote === "nay" ? "negative" : "neutral"}`} style={{ animationDelay: `${delay}ms` }}>
    {vote === "not_voting" ? "NV" : vote.toUpperCase()}
  </span>
);

/* ---------------- Forecast by member (35) ---------------- */

const SORTS: { id: MemberSort; label: string }[] = [
  { id: "persuadable", label: "Most persuadable first" }, { id: "money", label: "Most money first" }, { id: "forecast", label: "Likeliest first" },
];
const SHOWN = 4;

export function ForecastByMember({ store, whatIfAllowed }: { store: LegislationStore; whatIfAllowed: boolean }) {
  const rows = useRows(store);
  const { client, perspective, myPosition } = useClient(store);
  const { whatIfOn, overrides, hoverGroup, filterGroup, sort, forecast } = useLegislation(store, useShallow((s) => ({
    whatIfOn: s.whatIfOn, overrides: s.overrides, hoverGroup: s.hoverGroup, filterGroup: s.filterGroup, sort: s.sort, forecast: s.detail?.forecast,
  })));
  const [open, setOpen] = useState<Partial<Record<ForecastLabel, boolean>>>({});
  const [tip, setTip] = useState<ForecastLabel | null>(null);
  const needed = forecast?.needed ?? 26;
  const sorted = useMemo(() => sortMembers(rows, sort), [rows, sort]);
  const groups = useMemo(() => (client ? groupByLabel(sorted, perspective, client.position, myPosition) : null), [sorted, perspective, client, myPosition]);
  const proj = useMemo(() => (client ? projectWithWhatIf(rows, client.position, overrides, needed) : null), [rows, client, overrides, needed]);
  const base = useMemo(() => (client ? projectWithWhatIf(rows, client.position, {}, needed) : null), [rows, client, needed]);
  useEffect(() => {
    if (!hoverGroup) { setTip(null); return; }
    const t = setTimeout(() => setTip(hoverGroup), recipes.dimOthers.tooltipDelay);
    return () => clearTimeout(t);
  }, [hoverGroup]);
  if (!client || !groups || !proj || !base) return null;
  const way = perspective === "their" ? "their way" : perspective === "your" ? "your way" : "yes";
  const title = { tossup: "Toss-ups", unlikely: `Unlikely ${way}`, likely: `Likely ${way}` } as const;
  const changed = Object.keys(overrides).length > 0;
  const abbr = client.name.split(/\s+/).map((w) => w[0]).join("");
  const nextSort = SORTS[(SORTS.findIndex((x) => x.id === sort) + 1) % SORTS.length];
  const tipNames = tip ? groups[tip].map((x) => x.row.name.split(" ").filter((w) => !/^(Jr\.?|[A-Z]\.)$/.test(w)).pop()) : [];

  return (
    <section className="lg-bymember">
      <div className="row-between">
        {myPosition ? (
          <ViewSwitch label="Perspective" value={perspective === "their" ? "their" : "your"} onChange={(v) => store.getState().setPerspective(v)}
            options={[{ id: "your", label: `Your way · you ${myPosition}` }, { id: "their", label: `Their way · ${abbr} ${client.position}s` }]} />
        ) : <span />}
        <div className="row-gap">
          <button className="btn" onClick={() => store.getState().setSort(nextSort.id)} title={`Sort: ${nextSort.label}`}><ArrowDownUp size={14} aria-hidden />{SORTS.find((x) => x.id === sort)!.label}</button>
          {whatIfAllowed && <button className="btn" aria-pressed={whatIfOn} onClick={() => store.getState().toggleWhatIf()}><FlaskConical size={14} aria-hidden />What-if mode</button>}
        </div>
      </div>

      <div className="projection-card">
        <div className="row-between">
          <strong className="lg-proj__title">
            Projected {changed ? <>{base.yes}–{base.no} <ArrowRight size={14} aria-hidden className="lg-proj__arrow" /> <Roll value={`${proj.yes}–${proj.no}`} /></> : <Roll value={`${proj.yes}–${proj.no}`} />} · needs {needed} · range {proj.range[0]} to {proj.range[1]}
          </strong>
          {forecast && <TensionGauge t={forecast.tension} label={`${forecast.topic[0].toUpperCase()}${forecast.topic.slice(1)} tension · ${forecast.tension >= 0.5 ? "High" : forecast.tension >= 0.25 ? "Medium" : "Low"} · ${forecast.tension.toFixed(2)}`} strong />}
        </div>
        <div className="lg-proj__bar">
          <ProjectionBar likelyYes={groups.likely.length} tossup={groups.tossup.length} likelyNo={groups.unlikely.length}
            labels={[`Likely ${way}`, "Toss-up", `Unlikely ${way}`]} hover={hoverGroup}
            onHover={(g) => store.getState().setHoverGroup(g)}
            onSelect={(g) => store.getState().setFilterGroup(g)} />
          {tip && groups[tip].length > 0 && (
            <div className="lg-proj__tip" role="tooltip" data-group={tip}>
              <b>{groups[tip].length} {tip === "tossup" ? "toss-ups" : title[tip].toLowerCase()}</b>
              <span>{tipNames.slice(0, 4).join(", ")}{tipNames.length > 4 ? ` and ${tipNames.length - 4} more` : ""} · click to filter</span>
            </div>
          )}
        </div>
        {whatIfOn && (
          <div className="lg-whatif">
            <span className="what-if-badge"><FlaskConical size={12} aria-hidden />What-if · not saved</span>
            <button className="lg-link lg-link--muted" onClick={() => store.getState().toggleWhatIf()}>Reset</button>
          </div>
        )}
      </div>

      {(["tossup", "unlikely", "likely"] as const).filter((g) => !filterGroup || filterGroup === g).map((g) => {
        const list = groups[g];
        const shown = open[g] || filterGroup === g ? list : list.slice(0, SHOWN);
        return (
          <section key={g} id={`group-${g}`} className="group" data-dim={hoverGroup && hoverGroup !== g ? "" : undefined}>
            <GroupLabel count={list.length}>{title[g]}</GroupLabel>
            <List as="div" variant="plain" className="leg-list">
              {shown.map(({ row }, i) => <MemberRow key={row.personId} store={store} personId={row.personId} whatIf={whatIfOn} index={i} />)}
            </List>
            {list.length > shown.length && (
              <div className="lg-more"><LinkArrow onClick={() => setOpen((o) => ({ ...o, [g]: true }))}>{list.length - shown.length} more{g === "tossup" ? " toss-ups" : ""}</LinkArrow></div>
            )}
          </section>
        );
      })}
    </section>
  );
}

const MemberRow = memo(function MemberRow({ store, personId, whatIf, index }: { store: LegislationStore; personId: string; whatIf: boolean; index: number }) {
  const recordId = useLegislation(store, (s) => s.detail?.recordId); // "Flag as inaccurate" names the forecast (BE1)
  const row = useLegislation(store, (s) => s.detail?.lobbying.influence[s.clientId ?? ""]?.find((r) => r.personId === personId));
  const override = useLegislation(store, (s) => s.overrides[personId]);
  const moved = useLegislation(store, (s) => s.moved[personId]);
  const expanded = useLegislation(store, (s) => s.expandedId === personId);
  const vote = useLegislation(store, (s) => s.detail?.rollCall?.votes[personId]);
  const revealed = useLegislation(store, (s) => s.revealedAt);
  const topic = useLegislation(store, (s) => s.detail?.forecast?.topic ?? "this topic");
  const { client, perspective, myPosition } = useClient(store);

  // L2: the "+6 pts · reason" tag fades out after 4s
  useEffect(() => {
    if (!moved) return;
    const t = window.setTimeout(() => store.getState().clearMoved(personId), recipes.forecastMove.tagHoldMs + recipes.forecastMove.tagFade);
    return () => window.clearTimeout(t);
  }, [moved, personId, store]);

  if (!row || !client) return null;
  const f = inPerspective(row.forecast, perspective, client.position, myPosition);
  const way = perspective === "their" ? "their" : "your";
  const pts = moved ? movedPoints(inPerspective({ ...row.forecast, p: moved.from }, perspective, client.position, myPosition).p, f.p) : null;
  const sub = `Ward ${row.ward} · ${leaningLabel(row.leaning)} · ${canComputeMoneyLinked(client.positionBasis) && row.moneyLinked.n ? `${row.moneyLinked.k} of ${row.moneyLinked.n}${row.moneyLinked.n < 2 ? " · too few" : " money-linked their way"}` : "no money-linked votes"}`;
  const yesP = client.position === "support" ? row.forecast.p : 1 - row.forecast.p;

  return (
    <>
      {/* the row toggles its "why" panel on click; tabIndex 0 keeps it a j/k stop */}
      <ListRow as="div" className="member-row" data-row={personId} data-whatif={override ? "" : undefined}
        tabIndex={0} onClick={(e: React.MouseEvent) => { if (!(e.target as HTMLElement).closest("button")) store.getState().toggleExpanded(personId); }}>
        <RowLead><Face name={row.name} photoUrl={row.photoUrl} size={30} /></RowLead>
        <RowMain min={140} className="member-row__who">
          <RowTitle><RowToggle expanded={expanded} onToggle={() => store.getState().toggleExpanded(personId)}><b>{row.name}</b></RowToggle></RowTitle>
          <RowSub><small>{sub}</small></RowSub>
          {pts !== null && moved && (
            <span className={`moved-tag ${pts > 0 ? "is-up" : "is-down"}`}>
              {pts > 0 ? <ArrowUp size={12} aria-hidden /> : <ArrowDown size={12} aria-hidden />}{pts > 0 ? "+" : "−"}{Math.abs(pts)} pts · {moved.reason}
            </span>
          )}
        </RowMain>
        {vote ? (<>
          <RowCell hide="phone" align="end" className="lg-member__was">forecast {pct(yesP)}% yes</RowCell>
          <RowCell width={150} align="end"><VoteChip vote={vote} delay={revealed ? index * recipes.recordReveal.stagger : 0} /></RowCell>
        </>) : (<>
          <RowCell>{enoughHistory(row.inputs) ? <ForecastMeter f={f} perspective={way} /> : <NotEnoughHistory />}</RowCell>
          {whatIf ? (
            <RowCell width={150} className="member-row__seg">
              <div className="seg" role="radiogroup" aria-label={`What-if vote for ${row.name}`}>
                {(["yea", "nay", null] as const).map((v) => (
                  <button key={String(v)} role="radio" aria-checked={(override ?? null) === v} onClick={() => store.getState().setOverride(personId, v)}>
                    {v === "yea" ? "Yes" : v === "nay" ? "No" : "Forecast"}
                  </button>
                ))}
              </div>
            </RowCell>
          ) : <RowCell width={150} hide="phone"><ForecastChip label={f.label} perspective={way} className="lg-member__chip" text={undefined} key={`${f.label}-${way}`} /></RowCell>}
        </>)}
      </ListRow>
      {/* On phones the "why" is a bottom sheet (screenshots/55): tapping outside it (this wrapper's scrim) closes it. */}
      {expanded && <div className="why-row" onClick={(e) => { if (e.target === e.currentTarget) store.getState().toggleExpanded(personId); }}>
        <WhyPanel row={row} f={f} way={way} topic={topic} inferred={client.positionBasis === "inferred"} recordId={recordId} /></div>}
    </>
  );
});

/* ---------------- Whip count rows (28, office) ---------------- */

export function WhipList({ rows, clientPosition, count = 3 }: { rows: InfluenceRow[]; clientPosition: "support" | "oppose"; count?: number }) {
  const toss = sortMembers(rows, "persuadable").map((r) => ({ r, f: inPerspective(r.forecast, "yes", clientPosition) })).filter((x) => x.f.label === "tossup").slice(0, count);
  return (
    <List as="div" variant="plain" className="lg-whip">
      {toss.map(({ r, f }) => (
        <ListRow as="div" key={r.personId} className="lg-whip__row">
          <RowLead><Face name={r.name} photoUrl={r.photoUrl} size={30} /></RowLead>
          <RowMain min={100} className="member-row__who"><RowTitle><b>{r.name}</b></RowTitle><RowSub><small>Ward {r.ward}</small></RowSub></RowMain>
          <RowTrail><ForecastChip label="tossup" perspective="yes" text={`yes · ${pct(f.p)}%`} /></RowTrail>
        </ListRow>
      ))}
    </List>
  );
}
